import { Auth } from 'aws-amplify';
import appConfig from '../config/app-config';

const API = appConfig.apis.eventingApiEnv;

export class EventingApiError extends Error {
    constructor(public status: number, message: string, public data?: unknown) {
        super(message);
    }
}

export interface ApiMeta {
    apiVersion: string;
    apiName: string;
    environment: string;
    [key: string]: unknown;
}

export interface Enveloped<T> {
    data: T;
    meta: ApiMeta;
}

export interface OutboxSummary {
    sourceService: string;
    pending: number;
    oldestOccurredAt: string | null;
    maxAttempts: number;
}

export interface TopologySubscription {
    consumer: string;
    subscriptionArn: string;
    protocol: string;
    endpoint: string;
    filterPolicy: Record<string, unknown> | null;
    rawMessageDelivery: boolean;
    deliveryIssue: 'RAW_DELIVERY_DISABLED' | 'DIRECT_LAMBDA' | null;
    deliveryIssueFormatted: string | null;
    queueName: string | null;
    visibleMessages: number | null;
    inFlightMessages: number | null;
    deadLetterQueueName: string | null;
    deadLetterMessages: number | null;
}

export interface Topology {
    topicArn: string;
    consumers: string[];
    subscriptions: TopologySubscription[];
    outbox: OutboxSummary[];
}

export interface HistoryEvent {
    message: {
        eventId: string;
        eventType: string;
        occurredAt: string;
        correlationId: string;
        causationId: string;
        delivery: string;
        deliveryFormatted: string;
        target: string | null;
        payloadRef: string | null;
    };
    aggregate: {
        aggregateId: string;
        aggregateVersion: number;
    };
    payload: unknown;
    publishedAt: string;
}

export interface EventFilters {
    eventType?: string;
    aggregateId?: string;
    eventId?: string;
    from?: string;
    to?: string;
    size?: number;
}

export interface DeliveryReceipt {
    consumer: string;
    eventId: string;
    eventType: string;
    delivery: string;
    deliveryFormatted: string;
    outcome: string;
    outcomeFormatted: string;
    aggregateId: string;
    aggregateVersion: number;
    processedAt: string;
}

export interface OutboxEntry {
    eventId: string;
    sourceService: string;
    eventType: string;
    occurredAt: string;
    delivery: string;
    deliveryFormatted: string;
    target: string | null;
    aggregateId: string;
    aggregateVersion: number;
    attempts: number;
}

export interface DeadLetterQueue {
    consumer: string;
    queueName: string;
    deadLetterQueueName: string;
    deadLetterQueueArn: string;
    messages: number;
}

export interface DeadLetterMessage {
    messageId: string;
    attributes: Record<string, string | null>;
    body: unknown;
    receiveCount: number;
    sentAt: string | null;
}

export interface ReplayPayload {
    eventIds: string[];
    target: string;
    from: string;
    to: string;
}

export interface ReplayResult {
    target: string;
    published: string[];
    failed: string[];
    notFound: string[];
}

export interface SnapshotRepublicationPayload {
    sourceService: string;
    aggregateType: string;
    aggregateIds: string[];
    target: string;
}

export interface SnapshotRepublicationResult extends SnapshotRepublicationPayload {
    requestedBy: string | null;
}

export interface RedriveResult {
    consumer: string;
    deadLetterQueueName: string;
    queueName: string;
    taskHandle: string;
}

const headers = async (): Promise<Record<string, string>> => {
    const session = await Auth.currentSession();
    return {
        'Content-Type': 'application/json',
        'x-api-key': API.key,
        Authorization: `Bearer ${session.getIdToken().getJwtToken()}`,
    };
};

const request = async <T>(path: string, init: RequestInit = {}): Promise<Enveloped<T>> => {
    const response = await fetch(`${API.address}${path}`, { ...init, headers: await headers() });
    const text = await response.text();
    const body = text ? JSON.parse(text) : null;
    if (!response.ok) {
        const fieldMessages = (body?.errors ?? []).map((error: { fieldName: string; message: string }) => `${error.fieldName}: ${error.message}`);
        const message = [body?.message ?? `Erro ${response.status} na API do eventing`, ...fieldMessages].join(' · ');
        throw new EventingApiError(response.status, message, body);
    }
    return body as Enveloped<T>;
};

const post = <T>(path: string, data?: unknown) =>
    request<T>(path, { method: 'POST', body: data === undefined ? undefined : JSON.stringify(data) });

const query = (params: Record<string, string | number | undefined>) => {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== '') search.set(key, String(value));
    });
    const text = search.toString();
    return text ? `?${text}` : '';
};

export const eventingApi = {
    getTopology: () => request<Topology>('/topology'),

    listEvents: (filters: EventFilters) =>
        request<HistoryEvent[]>(
            `/events${query({
                'filter[eventType]': filters.eventType,
                'filter[aggregateId]': filters.aggregateId,
                'filter[eventId]': filters.eventId,
                'filter[occurredAt][gte]': filters.from,
                'filter[occurredAt][lte]': filters.to,
                size: filters.size,
            })}`,
        ),

    listDeliveries: (eventId: string) => request<DeliveryReceipt[]>(`/events/${encodeURIComponent(eventId)}/deliveries`),

    listOutboxEntries: (params: { sourceService?: string; page?: number; size?: number }) =>
        request<OutboxEntry[]>(
            `/outbox-entries${query({ 'filter[sourceService]': params.sourceService, page: params.page, size: params.size })}`,
        ),

    listDeadLetterQueues: () => request<DeadLetterQueue[]>('/dead-letter-queues'),

    listDeadLetterMessages: (consumer: string) =>
        request<DeadLetterMessage[]>(`/dead-letter-queues/${encodeURIComponent(consumer)}/messages`),

    createReplay: (payload: ReplayPayload) => post<ReplayResult>('/replays', payload),

    createSnapshotRepublication: (payload: SnapshotRepublicationPayload) =>
        post<SnapshotRepublicationResult>('/snapshot-republications', payload),

    createRedrive: (consumer: string) => post<RedriveResult>(`/dead-letter-queues/${encodeURIComponent(consumer)}/redrives`),
};
